import React from "react";
import { useData } from "../lib/store.jsx";
import {
  Panel, Donut, DualBarLine, Timeline, Dial, RoomMap,
  FeeCard, BenchBars, StoryCard, MiniBars, VersusBars,
} from "../components/ui.jsx";
import {
  gbp0, gbpM, gbpCompact, pct2, REGISTERED_BEDS, COMPANY, PAY,
  MILESTONES, BENCH,
} from "../lib/data.js";

const pc0 = (v) => (v == null ? "—" : `${Math.round(v)}%`);

export default function Overview() {
  const { d, m } = useData();
  const yrs = new Date().getFullYear() - COMPANY.founded;

  const annual = m.annual
    .filter((a) => a.revenue != null)
    .map((a) => ({ x: a.fy, EBITDA: Math.round(a.ebitda),
      Revenue: Math.round(a.revenue), ytd: a.ytd }));

  const mix = m.revenueMix.map((r) => ({
    name: r.name, value: Math.round(r.weekly) }));

  const pb = m.pbLatest || {};

  const ebitdaVsBench = m.ebitdaPerBed && BENCH.ebitdaPerBed
    ? Math.round((m.ebitdaPerBed / BENCH.ebitdaPerBed - 1) * 100) : null;

  // Falls: average per month, by calendar year (the safety story).
  const byYear = {};
  for (const f of d.falls) (byYear[f.year] ||= []).push(f.count);
  const fallsYearly = Object.entries(byYear).map(([y, arr]) => ({
    x: +y >= m.fallsLateYear ? `'${String(y).slice(2)} YTD` : `'${String(y).slice(2)}`,
    year: +y, v: +(arr.reduce((t, c) => t + c, 0) / arr.length).toFixed(1),
  })).sort((p, q) => p.year - q.year);
  const fBase = fallsYearly[0];
  // Compare like with like: the headline % is base year → last full year.
  const fNow = fallsYearly.find((f) => f.year === m.fallsRedFullYear);

  const ebitdaBars = annual.map((a) => ({ x: a.x, v: a.EBITDA }));

  const occupied = new Set(d.residents.map((r) => String(r.room)));

  const fees = d.residents.map((r) => ({
    fund: r.funding_type || "Other", fee: +r.weekly_fee || 0 }))
    .filter((r) => r.fee > 0);

  const bench = [
    { label: "EBITDA / bed", value: m.ebitdaPerBed, bench: BENCH.ebitdaPerBed,
      fmt: gbpCompact },
    { label: "Revenue / bed", value: m.revPerBed, bench: BENCH.revPerBed,
      fmt: gbpCompact },
    { label: "Gross profit / bed", value: pb.gpPerBed, bench: BENCH.gpPerBed,
      fmt: gbpCompact },
    { label: "EBITDA margin", value: pb.ebitdaMarginPct,
      bench: BENCH.ebitdaMarginPct, fmt: (v) => `${Math.round(v)}%` },
  ];

  return (
    <>
      <p className="story-lead s12">
        Since 2023 Aria has bucked the sector trend in every department —
        beating every industry benchmark, from employee retention to EBITDA
        per bed.
      </p>

      <StoryCard q="Is it full?"
        value={m.occYtdPct == null ? "—" : `${m.occYtdPct.toFixed(1)}%`}
        bench="occupancy this financial year"
        line={`${m.daysAtFull} nights completely full so far this year.`}>
        <Dial pct={m.occupancyPct} size={190}
          value={`${m.curBeds}/${REGISTERED_BEDS}`} label="beds tonight" />
      </StoryCard>

      <StoryCard q="Does it make money?"
        value={gbpCompact(m.ebitdaPerBed)}
        bench={`EBITDA per bed · sector ${gbpCompact(BENCH.ebitdaPerBed)}`
          + (ebitdaVsBench != null ? ` (+${ebitdaVsBench}%)` : "")}
        line={m.cagrPct == null ? "Annual EBITDA by financial year."
          : `Revenue up ${m.cagrPct.toFixed(1)}% a year since 2015 `
            + `(+${Math.round(m.totalGrowthPct)}% in total).`}>
        <MiniBars data={ebitdaBars} fmt={gbpM} />
      </StoryCard>

      <StoryCard q="Will the team stay?"
        value={m.turnoverTtm == null ? "—" : pct2(m.turnoverTtm)}
        bench={`staff turnover, last 12 months · sector ~${BENCH.turnoverPct}%`}
        line={(m.daysWithoutLeaver == null ? ""
          : `${m.daysWithoutLeaver} days since anyone left. `)
          + `No agency staff since April 2023. Pay from £${PAY.min.toFixed(2)} an hour.`}>
        <VersusBars rows={[
          { label: "Aria", text: m.turnoverTtm == null ? "—" : pct2(m.turnoverTtm),
            v: m.turnoverTtm || 0, lime: true },
          { label: "Sector average", text: `~${BENCH.turnoverPct}%`,
            v: BENCH.turnoverPct },
        ]} />
      </StoryCard>

      <StoryCard q="Is it safe?"
        value={m.fallsRedPct == null ? "—"
          : `${Math.round(Math.abs(m.fallsRedPct))}%`}
        bench={`fewer falls than ${m.fallsBaseYear}`}
        line={fBase && fNow
          ? `${fBase.v} falls a month in ${fBase.year}; ${fNow.v} a month in `
            + `${fNow.year}, with the home full.` : "Average falls per month, by year."}>
        <MiniBars data={fallsYearly} fmt={(v) => v.toFixed(1)} />
      </StoryCard>

      <Panel className="s8" title="Revenue and EBITDA"
        sub="each financial year — the latest year is lit">
        <DualBarLine data={annual} x="x" barKey="EBITDA" lineKey="Revenue"
          barName="EBITDA" lineName="Revenue" fmt={gbpM} h={280} />
      </Panel>
      <Panel className="s4" title="Per bed vs LaingBuisson"
        sub="the white mark is the sector figure">
        <BenchBars rows={bench} />
      </Panel>

      <Panel className="s4" title="Rooms tonight" sub="live from the fee list">
        <RoomMap occupied={occupied} registered={REGISTERED_BEDS} />
      </Panel>
      <Panel className="s4" title="Average fee"
        sub="weekly fee across current residents">
        <FeeCard fees={fees} />
      </Panel>
      <Panel className="s4" title="Who pays" sub="weekly fees by funding type">
        <Donut data={mix} unit=" £/wk" h={250} />
      </Panel>

      <Panel className="s12" title="Company milestones"
        sub={`${yrs} years operating, since ${COMPANY.founded}`}>
        <Timeline items={MILESTONES} />
      </Panel>
    </>
  );
}
